import {
  Package,
  Truck,
  DollarSign,
  Receipt,
  Award,
  AlertCircle,
  CheckCircle,
  Bell,
} from 'lucide-react'

export type NotificationType =
  | 'ORDER_ASSIGNED'
  | 'ORDER_DISPATCHED'
  | 'ORDER_STATUS_CHANGED'
  | 'PAYMENT_REQUIRED'
  | 'PAYMENT_CONFIRMED'
  | 'CERTIFICATE_READY'
  | 'INVOICE_ISSUED'
  | 'EXCEPTION_REPORTED'
  | 'EXCEPTION_RESOLVED'
  | 'SYSTEM'

export interface NotificationTypeConfig {
  label: string
  color: 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray'
  icon: React.ElementType
  priority: 'high' | 'medium' | 'low' // 优先级（用于排序）
}

export const NOTIFICATION_TYPE_CONFIG: Record<NotificationType, NotificationTypeConfig> = {
  ORDER_ASSIGNED: {
    label: '订单分配',
    color: 'blue',
    icon: Package,
    priority: 'medium',
  },
  ORDER_DISPATCHED: {
    label: '派单通知',
    color: 'blue',
    icon: Truck,
    priority: 'medium',
  },
  ORDER_STATUS_CHANGED: {
    label: '状态变更',
    color: 'purple',
    icon: Package,
    priority: 'low',
  },
  PAYMENT_REQUIRED: {
    label: '待支付',
    color: 'orange',
    icon: DollarSign,
    priority: 'high',
  },
  PAYMENT_CONFIRMED: {
    label: '支付确认',
    color: 'green',
    icon: CheckCircle,
    priority: 'medium',
  },
  CERTIFICATE_READY: {
    label: '证书就绪',
    color: 'green',
    icon: Award,
    priority: 'medium',
  },
  INVOICE_ISSUED: {
    label: '发票开具',
    color: 'blue',
    icon: Receipt,
    priority: 'medium',
  },
  EXCEPTION_REPORTED: {
    label: '异常报告',
    color: 'red',
    icon: AlertCircle,
    priority: 'high',
  },
  EXCEPTION_RESOLVED: {
    label: '异常解决',
    color: 'green',
    icon: CheckCircle,
    priority: 'low',
  },
  SYSTEM: {
    label: '系统通知',
    color: 'gray',
    icon: Bell,
    priority: 'low',
  },
}

/**
 * 获取通知类型的中文标签
 */
export function getNotificationTypeLabel(type: NotificationType): string {
  return NOTIFICATION_TYPE_CONFIG[type]?.label || type
}

/**
 * 获取通知类型的颜色
 */
export function getNotificationTypeColor(type: NotificationType): string {
  const colorMap = {
    blue: 'text-blue-600 bg-blue-50',
    green: 'text-green-600 bg-green-50',
    orange: 'text-orange-600 bg-orange-50',
    red: 'text-red-600 bg-red-50',
    purple: 'text-purple-600 bg-purple-50',
    gray: 'text-gray-600 bg-gray-50',
  }

  const color = NOTIFICATION_TYPE_CONFIG[type]?.color || 'gray'
  return colorMap[color]
}

/**
 * 获取通知类型的图标
 */
export function getNotificationTypeIcon(type: NotificationType) {
  return NOTIFICATION_TYPE_CONFIG[type]?.icon || Bell
}

/**
 * 判断是否为重要通知（高优先级）
 */
export function isImportantNotification(type: NotificationType): boolean {
  return NOTIFICATION_TYPE_CONFIG[type]?.priority === 'high'
}

export const NOTIFICATION_MODULE_LABELS: Record<string, string> = {
  ORDER: '订单',
  VISIT: '上门派单',
  LAB: '邮寄检测',
  CERTIFICATE: '证书',
  PAYMENT: '收款',
  INVOICE: '开票',
  REFUND: '退款',
  EXCEPTION: '异常',
  SYSTEM: '系统',
}

export const NOTIFICATION_EVENT_LABELS: Record<string, string> = {
  ORDER_PENDING_ASSIGN: '新增订单待处理',
  ORDER_ASSIGNED: '订单已分配',
  ORDER_DISPATCHED: '订单已派单',
  ORDER_STATUS_CHANGED: '订单状态变更',
  PAYMENT_REQUIRED: '订单待支付',
  PAYMENT_CONFIRMED: '到款已确认',
  PAYMENT_VOUCHER_SUBMITTED: '付款凭证已提交',
  CERTIFICATE_UPLOAD_REQUIRED: '证书待上传',
  CERTIFICATE_READY: '证书已就绪',
  INVOICE_ISSUED: '发票已开具',
  REFUND_PENDING: '退款待处理',
  REFUND_COMPLETED: '退款已完成',
  REFUND_REJECTED: '退款已驳回',
  EXCEPTION_REPORTED: '新增异常待处理',
  EXCEPTION_RESOLVED: '异常已解决',
  SYSTEM: '系统通知',
}

export function getNotificationModuleLabel(module: string): string {
  return NOTIFICATION_MODULE_LABELS[module] || module || '系统'
}

export function getNotificationEventLabel(eventType: string): string {
  return NOTIFICATION_EVENT_LABELS[eventType] || eventType || '系统通知'
}
